import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
 ClipboardList, ArrowLeft, Calendar, Scale, Droplets, Clock, 
 TrendingUp, Eye, Trash2, CheckCircle, XCircle, AlertCircle, Sparkles
} from 'lucide-react';
import ToolSelector from '../components/ToolSelector';
import PageTutorial from '../components/PageTutorial';

const BatchHistory = () => {
 const navigate = useNavigate();
 const { API } = useContext(AuthContext);
 
 const [batches, setBatches] = useState([]);
 const [loading, setLoading] = useState(true);
 const [selectedBatch, setSelectedBatch] = useState(null);
 const [showDetailModal, setShowDetailModal] = useState(false);

 // Fetch all saved batches
 useEffect(() => {
 const fetchBatches = async () => {
 try {
 const response = await axios.get(`${API}/batch-tracker/logs`);
 setBatches(response.data.logs || []);
 } catch (error) {
 console.error('Error fetching batches:', error);
 toast.error('Failed to load batch history');
 } finally {
 setLoading(false);
 }
 };
 fetchBatches();
 }, [API]);

 // Calculate aggregate metrics
 const calculateMetrics = () => {
 let totalWaterRemoved = 0;
 let totalCycleTime = 0;
 let totalBatches = batches.length;
 let completedBatches = 0;
 let passedTrays = 0;
 let failedTrays = 0;

 batches.forEach(batch => {
 const trays = batch.trays || [];
 let batchCompleted = false;

 trays.forEach(tray => {
 const wetWeight = parseFloat(tray.wet_weight_g) || 0;
 const actualDry = parseFloat(tray.actual_dry_weight_g) || 0;
 const proposedDry = parseFloat(tray.proposed_dry_weight_g) || 0;

 // Water removed = wet weight - actual dry weight
 if (wetWeight > 0 && actualDry > 0) {
 const waterRemoved = wetWeight - actualDry;
 totalWaterRemoved += waterRemoved;
 batchCompleted = true;

 // Check pass/fail
 if (actualDry <= proposedDry) {
 passedTrays++;
 } else {
 failedTrays++;
 }
 }
 });

 if (batchCompleted) {
 completedBatches++;
 // Assume average cycle time of 24 hours per batch
 totalCycleTime += 24;
 }
 });

 return {
 totalWaterRemoved: (totalWaterRemoved / 453.592).toFixed(2), // Convert to lbs
 totalCycleTime,
 totalBatches,
 completedBatches,
 inProgressBatches: totalBatches - completedBatches,
 passedTrays,
 failedTrays,
 successRate: passedTrays + failedTrays > 0 ? ((passedTrays / (passedTrays + failedTrays)) * 100).toFixed(1) : 0
 };
 };

 const metrics = calculateMetrics();

 // View batch details
 const viewBatchDetails = (batch) => {
 setSelectedBatch(batch);
 setShowDetailModal(true);
 };

 // Open batch in tracker
 const openInTracker = (batchId) => {
 navigate(`/batch-tracker?load=${batchId}`);
 };

 // Delete batch
 const deleteBatch = async (batchId) => {
 if (!window.confirm('Are you sure you want to delete this batch? This cannot be undone.')) {
 return;
 }

 try {
 await axios.delete(`${API}/batch-tracker/${batchId}`);
 toast.success('Batch deleted successfully');
 setBatches(batches.filter(b => b.id !== batchId));
 } catch (error) {
 console.error('Delete error:', error);
 toast.error('Failed to delete batch');
 }
 };

 // Get batch status
 const getBatchStatus = (batch) => {
 const trays = batch.trays || [];
 const completedTrays = trays.filter(t => parseFloat(t.actual_dry_weight_g) > 0).length;
 
 if (completedTrays === 0) return { label: 'Not Started', color: 'bg-slate-100 text-slate-700', icon: AlertCircle };
 if (completedTrays < trays.length) return { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: Clock };
 return { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle };
 };

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <ToolSelector />
 <div className="container mx-auto px-4 py-8 max-w-7xl">
 {/* Header */}
 <div className="mb-8">
 <Button
 variant="ghost"
 onClick={() => navigate('/batch-tracker')}
 className="mb-4"
 >
 <ArrowLeft className="w-4 h-4 mr-2" />
 Back to Batch Tracker
 </Button>
 <div className="flex items-center gap-4 mb-4">
 <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
 <ClipboardList className="w-7 h-7" />
 </div>
 <div>
 <span className="eyebrow">Batches</span>
 <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 text-foreground">Batch History</h1>
 <p className="text-muted-foreground text-sm">
 Every batch you&apos;ve run — search, review, and reuse.
 </p>
 </div>
 </div>
 </div>

 <PageTutorial id="batch_tracker" />

 {/* Compact liability advisory */}
 <div className="mb-6 rounded-2xl border border-primary/40 bg-primary/5 p-4">
 <p className="text-sm text-foreground/80 leading-relaxed">
 <strong className="text-foreground">Educational tool.</strong> Freeze Dry Academy is not
 liable for the outcome of any batch. You&apos;re responsible for equipment operation and
 food-safety practices.
 </p>
 </div>
 <div>
 </div>

 {/* Legacy amber advertisement removed — replaced by PageTutorial above. */}

 {/* Advertisement Banner - Header */}
 <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border-2 border-amber-300 dark:border-amber-700 shadow-sm">
 <div className="flex items-center justify-between gap-4">
 <div className="flex items-center gap-3">
 <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
 <Sparkles className="w-6 h-6 text-white" />
 </div>
 <div>
 <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
 🎓 Master Freeze-Drying with Our Online Courses!
 </p>
 <p className="text-xs text-amber-700 dark:text-amber-300">
 Learn from experts • Step-by-step tutorials • Community support
 </p>
 </div>
 </div>
 <Button
 size="sm"
 className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
 >
 Learn More
 </Button>
 </div>
 </div>

 {/* Aggregate Metrics */}
 <div className="grid md:grid-cols-4 gap-4 mb-8">
 <Card className="border-2 border-blue-300 dark:border-blue-700">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Total Batches</p>
 <p className="text-3xl font-bold text-blue-600">{metrics.totalBatches}</p>
 <p className="text-xs text-muted-foreground mt-1">
 {metrics.completedBatches} completed • {metrics.inProgressBatches} in progress
 </p>
 </div>
 <ClipboardList className="w-12 h-12 text-blue-600 opacity-20" />
 </div>
 </CardContent>
 </Card>

 <Card className="border-2 border-cyan-300 dark:border-cyan-700">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Water Removed</p>
 <p className="text-3xl font-bold text-secondary">{metrics.totalWaterRemoved} lbs</p>
 <p className="text-xs text-muted-foreground mt-1">
 {(parseFloat(metrics.totalWaterRemoved) * 0.453592).toFixed(1)} kg
 </p>
 </div>
 <Droplets className="w-12 h-12 text-secondary opacity-20" />
 </div>
 </CardContent>
 </Card>

 <Card className="border-2 border-primary/20">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Cycle Time</p>
 <p className="text-3xl font-bold text-primary">{metrics.totalCycleTime}h</p>
 <p className="text-xs text-muted-foreground mt-1">
 {(metrics.totalCycleTime / 24).toFixed(1)} days total
 </p>
 </div>
 <Clock className="w-12 h-12 text-primary opacity-20" />
 </div>
 </CardContent>
 </Card>

 <Card className="border-2 border-green-300 dark:border-green-700">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-muted-foreground">Success Rate</p>
 <p className="text-3xl font-bold text-green-600">{metrics.successRate}%</p>
 <p className="text-xs text-muted-foreground mt-1">
 {metrics.passedTrays} passed • {metrics.failedTrays} failed
 </p>
 </div>
 <TrendingUp className="w-12 h-12 text-green-600 opacity-20" />
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Batch List */}
 <Card>
 <CardHeader>
 <CardTitle>All Batches ({batches.length})</CardTitle>
 <CardDescription>Click on a batch to view details or open in tracker</CardDescription>
 </CardHeader>
 <CardContent>
 {loading ? (
 <p className="text-center py-8 text-muted-foreground">Loading batches...</p>
 ) : batches.length === 0 ? (
 <div className="text-center py-12">
 <ClipboardList className="w-16 h-16 mx-auto text-muted-foreground opacity-50 mb-4" />
 <p className="text-lg text-muted-foreground mb-4">No batches found</p>
 <Button onClick={() => navigate('/batch-tracker')} className="bg-gradient-to-r from-green-600 to-emerald-600">
 Create Your First Batch
 </Button>
 </div>
 ) : (
 <div className="space-y-3">
 {batches.map((batch) => {
 const status = getBatchStatus(batch);
 const StatusIcon = status.icon;
 const trays = batch.trays || [];
 const totalWetWeight = trays.reduce((sum, t) => sum + (parseFloat(t.wet_weight_g) || 0), 0);

 return (
 <div
 key={batch.id}
 className="p-4 border-2 border-slate-200 dark:border-slate-700 rounded-lg hover:border-primary/30 transition-all"
 >
 <div className="flex items-center justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-2">
 <h3 className="text-lg font-bold text-slate-900 dark:text-white">
 {batch.batch_name}
 </h3>
 <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.color} flex items-center gap-1`}>
 <StatusIcon className="w-3 h-3" />
 {status.label}
 </span>
 </div>
 <div className="grid md:grid-cols-4 gap-4 text-sm text-muted-foreground">
 <div className="flex items-center gap-2">
 <Calendar className="w-4 h-4" />
 {new Date(batch.created_at).toLocaleDateString()}
 </div>
 <div className="flex items-center gap-2">
 <Scale className="w-4 h-4" />
 {trays.length} trays
 </div>
 <div className="flex items-center gap-2">
 <Droplets className="w-4 h-4" />
 {(totalWetWeight / 453.592).toFixed(2)} lbs total
 </div>
 <div className="flex items-center gap-2">
 <Clock className="w-4 h-4" />
 {new Date(batch.updated_at).toLocaleTimeString()}
 </div>
 </div>
 </div>
 <div className="flex gap-2 ml-4">
 <Button
 size="sm"
 variant="outline"
 onClick={() => viewBatchDetails(batch)}
 className="border-blue-500 text-blue-700 hover:bg-blue-50"
 >
 <Eye className="w-4 h-4 mr-1" />
 View
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => openInTracker(batch.id)}
 className="border-green-500 text-green-700 hover:bg-green-50"
 >
 <ClipboardList className="w-4 h-4 mr-1" />
 Open
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={() => deleteBatch(batch.id)}
 className="border-red-500 text-red-700 hover:bg-red-50"
 >
 <Trash2 className="w-4 h-4" />
 </Button>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Advertisement Banner - Footer */}
 <div className="mt-8 p-6 bg-pro-soft rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
 <div className="text-center mb-4">
 <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">
 🌟 Join the Freeze Dry Academy Community
 </h3>
 <p className="text-sm text-blue-700 dark:text-blue-300">
 Connect with thousands of freeze-drying enthusiasts, share recipes, and get expert advice!
 </p>
 </div>
 <div className="grid md:grid-cols-3 gap-4">
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">📚</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-white">Premium Recipes</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Curated batch recipes</p>
 </div>
 </div>
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">👥</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-white">Expert Community</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Answers from real operators</p>
 </div>
 </div>
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">🎁</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-white">Exclusive Deals</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Vendor + partner perks</p>
 </div>
 </div>
 </div>
 <div className="mt-4 text-center">
 <Button className="bg-blue-600 hover:bg-blue-700 text-white">
 Join Community
 </Button>
 </div>
 </div>

 {/* Detail Modal */}
 {showDetailModal && selectedBatch && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-900 rounded-lg max-w-4xl w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-2xl font-bold">{selectedBatch.batch_name}</h3>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setShowDetailModal(false)}
 >
 ✕
 </Button>
 </div>

 <div className="space-y-4">
 {selectedBatch.trays.map((tray, index) => {
 const actualDry = parseFloat(tray.actual_dry_weight_g) || 0;
 const proposedDry = parseFloat(tray.proposed_dry_weight_g) || 0;
 const passed = actualDry > 0 && actualDry <= proposedDry;
 const completed = actualDry > 0;

 return (
 <div
 key={index}
 className={`p-4 rounded-lg border-2 ${
 !completed
 ? 'border-slate-300 bg-slate-50'
 : passed
 ? 'border-green-300 bg-green-50'
 : 'border-red-300 bg-red-50'
 }`}
 >
 <div className="flex items-center justify-between mb-3">
 <h4 className="font-bold text-lg">
 Tray {tray.tray_number}: {tray.product_name || 'Empty'}
 </h4>
 {completed && (
 <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
 passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
 }`}>
 {passed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
 {passed ? 'PASS' : 'FAIL'}
 </span>
 )}
 </div>
 <div className="grid md:grid-cols-3 gap-3 text-sm">
 <div>
 <p className="text-muted-foreground">Food Wet Weight</p>
 <p className="font-bold">{(parseFloat(tray.wet_weight_g) / 453.592).toFixed(2)} lbs</p>
 </div>
 <div>
 <p className="text-muted-foreground">Proposed Dry</p>
 <p className="font-bold">{(proposedDry / 453.592).toFixed(2)} lbs</p>
 </div>
 <div>
 <p className="text-muted-foreground">Actual Dry</p>
 <p className="font-bold">{actualDry > 0 ? (actualDry / 453.592).toFixed(2) : '0.00'} lbs</p>
 </div>
 </div>
 </div>
 );
 })}
 </div>

 <div className="mt-6 flex gap-3">
 <Button
 onClick={() => {
 setShowDetailModal(false);
 openInTracker(selectedBatch.id);
 }}
 className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
 >
 Open in Tracker
 </Button>
 <Button
 onClick={() => setShowDetailModal(false)}
 variant="outline"
 className="flex-1"
 >
 Close
 </Button>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default BatchHistory;
